import { faPencilAlt as solidPencilAlt } from "@jewlr/pro-solid-svg-icons/faPencilAlt"
import { faSpinner as solidSpinner } from "@jewlr/pro-solid-svg-icons/faSpinner"
import { faTrash as solidTrash } from "@jewlr/pro-solid-svg-icons/faTrash"
import Button from "@jewlr/storybook/core/button"
import PropTypes from "prop-types"
import React from "react"
import { connect } from "react-redux"
import styled from "styled-components"

import { updateChoice, trackSlide } from "areas/product/store/actions"
import { Box, Typography } from "components/common/index"
import FontAwesomeIcon from "components/font-awesome-icon"
import { openModal } from "components/modal"
import { jewlr_t } from "helpers/application"
import StorageHelper from "helpers/local-storage-cache"
import { removePref } from "helpers/user-prefs"

import ImageUploadBtn from "../image-upload-btn"

// imageState is one of CHOOSE, SHOW
const UploadArea = styled.div`
  font-size: 14px;
  letter-spacing: 0.5px;
  text-align: center;
`

const ImageContainer = styled.div`
  cursor: pointer;
  float: right;
  height: ${(props) => (props.theme.mediaQueries.tablet ? "145px" : "135px")};
  overflow: hidden;
  position: relative;
  width: ${(props) => (props.theme.mediaQueries.tablet ? "54%" : "52%")};
`

const Image = styled.img`
  float: right;
  height: 80%;
`

const LoadingSpinner = styled(FontAwesomeIcon)`
  animation: fa-spin 2s infinite linear;
  display: none;
  font-size: 28px;
  position: absolute;
  right: 10%;
  top: 25%;
  z-index: 5;
`

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
`

const EditBtn = styled(Button)`
  padding: 16px 16px;
`

const EditSpan = styled.span`
  padding-left: 3px;
`

const HandWritingMsg = styled(Typography).attrs({
  fontSize: { _: "14px", tablet: "12px" },
  fontWeight: 500,
  letterSpacing: 0,
  lineHeight: { _: "16px", tablet: "14px" },
  mb: { _: "30px", tablet: "20px" },
  textAlign: "left",
})``

const InternalImageWizardOptions = ({
  data,
  handwritingEngraving,
  imageState,
  imageUrl,
  photoDescription,
  photoTitle,
  openImageUploadModal,
  setChoice,
}) => {
  if (!data || data.disabled) {
    return null
  }

  return (
    <Box
      data-hashid={data.option.name}
      data-label={data.option.display_label}
      data-pref={data.pref}
      data-summary-field={data.option.display_label}
      mb="10px"
      p="10px"
    >
      {imageState == "SHOW" ? (
        <div data-pref={data.pref}>
          <ImageContainer
            data-pref={data.pref}
            onClick={() => {
              openImageUploadModal()
            }}
          >
            <Image
              alt="Photo preview"
              data-cy={`preview-${data.pref}`}
              data-pref={data.pref}
              id={`${data.option.name}_preview_img`}
              src={
                imageUrl || require("images/product/photo_pendant/default.jpg")
              }
            />
            <LoadingSpinner icon={solidSpinner} />
          </ImageContainer>
          <Box p="10px">
            <Typography
              color="grey.65"
              fontSize="14px"
              letterSpacing="0.5px"
              mb="14px"
              mt="6px"
            >
              {photoTitle}
            </Typography>
            <Typography fontSize="12px" mb="0">
              {photoDescription}
            </Typography>
            <Row>
              <EditBtn
                color="link"
                cursor="pointer"
                data-pref={data.pref}
                fontSize="12px"
                m="8px 0 0"
                onClick={() => {
                  openImageUploadModal()
                }}
                padding="0"
              >
                <FontAwesomeIcon icon={solidPencilAlt} />
                <EditSpan>Modify</EditSpan>
              </EditBtn>
              <EditBtn
                color="link"
                cursor="pointer"
                data-pref={data.pref}
                fontSize="12px"
                m="8px 0 0"
                onClick={() => {
                  const pref = data.pref
                  const width = data.cropper.aspect_ratio_width
                  const height = data.cropper.aspect_ratio_height
                  const cacheImageKey = `image-${pref}-${width}-${height}`
                  const cacheCropperKey = `cropper-${pref}-${width}-${height}`
                  StorageHelper.remove(cacheImageKey)
                  StorageHelper.remove(cacheCropperKey)
                  removePref(`${pref}_${width}_${height}`)
                  setChoice(pref, {
                    photoUploaded: false,
                    value: "",
                  })
                }}
                padding="0"
              >
                <FontAwesomeIcon icon={solidTrash} />
                <EditSpan>Remove</EditSpan>
              </EditBtn>
            </Row>
          </Box>
        </div>
      ) : (
        <UploadArea>
          {handwritingEngraving && (
            <HandWritingMsg>
              We use your image of a handwritten message to engrave your{" "}
              {jewlr_t("jewelry")}.{" "}
              <Button
                color="primary"
                onClick={() => {
                  openModal(`#pendant-upload-${data.pref}-tips`)
                }}
                underline
                variant="text"
              >
                Read image tips for best results.
              </Button>
            </HandWritingMsg>
          )}
          <ImageUploadBtn pref={data.pref} />
          {/*end pendant upload area*/}
          {/*Pendant photo preview content*/}
        </UploadArea>
      )}
    </Box>
  )
}

const getImageUrl = (data, display) => {
  if (display && display.image) {
    return display.image
  }
  if (data) {
    return require("images/product/photo_pendant/default.jpg")
  }
  return ""
}

const mapStateToProps = (state, ownProps) => {
  let data = state.wizard.data[ownProps.pref]
  let display = state.display[ownProps.pref] || {}
  let choices = state.choices[ownProps.pref]
  let imageState =
    choices && choices.value && choices.value.startsWith("user_uploads/")
      ? "SHOW"
      : "CHOOSE"
  let productType = state.wizard.description.product_type?.toLowerCase()
  let photoTitle = `YOUR ${productType.toUpperCase()} IMAGE`
  let photoDescription = `Your personal photo will be printed onto your ${productType}.`
  if (data?.option?.display_labels?.photo_title)
    photoTitle = data.option.display_labels.photo_title
  if (data?.option?.display_labels?.photo_description)
    photoDescription = data.option.display_labels.photo_description
  const handwritingEngraving =
    ownProps.pref && state.wizard.data[ownProps.pref]?.handwriting_engraving
  return {
    adminMode: state.wizard.adminMode || false,
    data,
    display: state.display,
    handwritingEngraving,
    imageState,
    imageUrl: getImageUrl(data, display),
    isMobile: state.browser.is.mobile,
    photoDescription,
    photoTitle,
    productType,
    wizardData: state.wizard.data,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    dispatch,
  }
}

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const { wizardData } = stateProps
  const { dispatch } = dispatchProps
  return {
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
    openImageUploadModal: () => {
      const pref = stateProps.data?.pref || "img1"
      const iwo = wizardData[pref]
      if (iwo?.track_slide) {
        dispatch(trackSlide(iwo.track_slide))
      }
      openModal(`#pendant-upload-${pref}`)
    },
    setChoice: (pref, data) => {
      dispatch(updateChoice(pref, data, true))
      try {
        if (typeof ownProps.callback !== "undefined" && ownProps.callback) {
          ownProps.callback()
        }
      } catch (error) {
        console.log("CALLBACK ERROR:", error)
      }
    },
  }
}

InternalImageWizardOptions.propTypes = {
  data: PropTypes.object,
  handwritingEngraving: PropTypes.bool,
  imageState: PropTypes.string,
  imageUrl: PropTypes.string,
  openImageUploadModal: PropTypes.func,
  photoDescription: PropTypes.string,
  photoTitle: PropTypes.string,
  setChoice: PropTypes.func,
}

const ImageWizardOptions = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(InternalImageWizardOptions)

export default ImageWizardOptions
